import { Suspense, lazy, useEffect } from 'react';

import { Helmet } from 'react-helmet';
import { connect } from 'react-redux';
import { Route } from 'react-router-dom';

import { PUBLIC_PAGES } from 'general/constants';
import TimeAgo from 'javascript-time-ago';
import en from 'javascript-time-ago/locale/en.json';
import { PermissionsContext, definePermissionsFor } from 'permissions';
import { useAppDispatch, useAppSelector } from 'redux/store/store.types';
import { initWebsocket } from 'utils/websocket';

import { ChatEventsTrigger, useChatEvents } from 'modules/app-chat';
import { AuthEvents, useAuthSession } from 'modules/auth-session';
import { setBrandingSettings } from 'modules/platform';

import UserIdDocumentModal from 'components/UserIdDocumentModal';

import withRoot from './MUITheme';
import NibLoader from './components/common/NibLoader/NibLoader';
import LoginPage from './pages/LoginPage/LoginPage';
import PasswordResetPage from './pages/PasswordResetPage/PasswordResetPage';
import Private, { Child } from './pages/Private';
import RegistrationPage from './pages/RegistrationPage/RegistrationPage';
import './sass/main.sass';

TimeAgo.addDefaultLocale(en);

const NAKED_PAGES = [];
const UNPROTECTED_PAGES = ['/'];

/* Async Components */
const CompaniesPageContainer = lazy(() => import('./pages/CompaniesPage/CompaniesPageContainer'));
const CompanyTypesPageContainer = lazy(() => import('./pages/CompanyTypesPage'));
const PlatformSettingsPageContainer = lazy(() => import('./pages/PlatformSettingsPage/PlatformSettingsPageContainer'));
const PlatformDocumentationPage = lazy(() => import('./pages/PlatformDocumentation/PlatformDocumentationPage'));
const PlatformDashboardPage = lazy(() => import('./pages/PlatformDashboard/DashboardPage'));
const UsersPageContainer = lazy(() => import('./pages/UsersPage/UsersPageContainer'));
const ImagesPageContainer = lazy(() => import('./pages/ImagesPage/ImagesPageContainer'));
const DocumentsPageContainer = lazy(() => import('./pages/DocumentsPage/DocumentsPageContainer'));

const UserProfilePage = lazy(() => import('./pages/UserProfilePage/UserProfilePage.tsx'));
const EecsGoPage = lazy(() => import('./pages/EecsGoPage/EecsGoPageContainer'));
const CryptoWalletPage = lazy(() => import('./pages/CryptoWallet/CryptoWalletContainer'));
const NotificationsPage = lazy(() => import('./pages/Notifications/NotificationsPageContainer'));
const ChatPage = lazy(() => import('./pages/Chat/ChatPageContainer'));

const AcceptedRoutes = () => {
  useChatEvents();

  return (
    <>
      <ChatEventsTrigger />

      <Route path="/platform-dashboard">
        <PlatformDashboardPage />
      </Route>

      <Route path="/docs">
        <PlatformDocumentationPage />
      </Route>

      <Route exact path="/notifications">
        <NotificationsPage />
      </Route>

      <Route exact path="/wallet">
        <CryptoWalletPage />
      </Route>

      <Route exact path="/communication">
        <ChatPage />
      </Route>

      <Route path="/companies">
        <CompaniesPageContainer />
      </Route>

      <Route path="/company_types">
        <CompanyTypesPageContainer />
      </Route>

      <Route path="/platform_settings">
        <PlatformSettingsPageContainer />
      </Route>

      <Route path="/users">
        <UsersPageContainer />
      </Route>

      <Route path="/images">
        <ImagesPageContainer />
      </Route>

      <Route path="/documents">
        <DocumentsPageContainer />
      </Route>

      <Route path="/user-profile">
        <UserProfilePage />
      </Route>

      <Route path="/eecs-go">
        <EecsGoPage />
      </Route>
    </>
  );
};

const addClass = (classname, element) => {
  var cn = element.className;
  if (cn.indexOf(classname) != -1) {
    return;
  }
  if (cn != '') {
    classname = ' ' + classname;
  }
  element.className = cn + classname;
};

const removeClass = (classname, element) => {
  var cn = element.className;
  var rxp = new RegExp('\\s?\\b' + classname + '\\b', 'g');
  cn = cn.replace(rxp, '');
  element.className = cn;
};

const onScroll = (appContainer) => {
  if (appContainer.scrollTop > 60) {
    addClass('header-size-s', appContainer);
  } else {
    removeClass('header-size-s', appContainer);
  }
};

const App = () => {
  initWebsocket();

  const dispatch = useAppDispatch();

  const { currentUser, isLoading: isSessionLoading } = useAppSelector((store) => store.authSession);
  const { navigationLinks, brandingSettings, loading: appLoading } = useAppSelector((state) => state.platform);

  const { checkAuth } = useAuthSession();

  const disabledLinks = navigationLinks.filter((link) => link.disabled).map((link) => link.to);
  const permissions = definePermissionsFor(currentUser);
  const appContainer = document.getElementById('app-wrap');

  useEffect(() => {
    const root = document.documentElement;
    root?.style.setProperty('--accent-color', brandingSettings.accent_color);
    root?.style.setProperty('--accent-text-color', brandingSettings.accent_text_color);
    root?.style.setProperty('--accent-color-r', brandingSettings.accentColorRGB.r);
    root?.style.setProperty('--accent-color-g', brandingSettings.accentColorRGB.g);
    root?.style.setProperty('--accent-color-g10', brandingSettings.accentColorRGB.g10);
    root?.style.setProperty('--accent-color-b', brandingSettings.accentColorRGB.b);
    document.title = brandingSettings.company_name;

    if (brandingSettings.favicon) {
      const favicon = document.getElementById('favicon');
      favicon.href = brandingSettings.favicon.url;
    }
    const titleElement = document.getElementById('adminPanelTitle');
    if (titleElement && brandingSettings?.admin_panel_name_alias) {
      titleElement.innerText = brandingSettings.admin_panel_name_alias;
    }
  }, [brandingSettings]);

  useEffect(() => {
    if (window?.APP_CONFIG) {
      dispatch(setBrandingSettings(window.APP_CONFIG));
    }
  }, [window?.APP_CONFIG]);

  useEffect(() => {
    checkAuth();

    const checkAuthEventHandler = async () => {
      if (document.visibilityState === 'visible') {
        await checkAuth();
      }
    };

    document.addEventListener('visibilitychange', checkAuthEventHandler);

    return () => {
      document.removeEventListener('visibilitychange', checkAuthEventHandler);
    };
  }, []);

  return (
    <div id="app-wrap" onScroll={() => onScroll(appContainer)} className="max-w-screen-app ml-auto mr-auto">
      {currentUser && <AuthEvents user={currentUser} />}
      <Suspense fallback={<NibLoader />}>
        <Helmet>
          <title>{brandingSettings?.admin_panel_name_alias}</title>
        </Helmet>
        {isSessionLoading || appLoading ? <NibLoader line={false} /> : ''}

        <Private
          publicRoutes={PUBLIC_PAGES}
          nakedRoutes={NAKED_PAGES}
          unprotectedRoutes={UNPROTECTED_PAGES}
          disabledRoutes={disabledLinks}
        >
          <Child type="Accepted">
            <PermissionsContext.Provider value={permissions}>
              <AcceptedRoutes />
            </PermissionsContext.Provider>
          </Child>

          <Child type="Denied">
            <Route exact path="/login">
              <LoginPage />
            </Route>
            <Route exact path="/password-reset">
              <PasswordResetPage />
            </Route>
            <Route exact path="/registration">
              <RegistrationPage />
            </Route>
          </Child>
        </Private>
        <UserIdDocumentModal />
      </Suspense>
    </div>
  );
};

export default connect(null, {})(withRoot(App));
